import React, { useCallback, useEffect, useState } from 'react';
import { Card, Table, Tag, Space, Button, Input, Tooltip, Typography, message } from 'antd';
import { ReloadOutlined } from '@ant-design/icons';
import { useTranslation } from '../../../shared/i18n';
import cdnProbeService from '../../services/cdnProbeService';

const { Text } = Typography;
const KIND_COLOR = { bgp: 'blue', split: 'orange', nobgp: 'default' };

/** 源站分组:同一条 BGP 宣告为一组;多点延迟指纹一致的组合并、偏离的 /24 拆出 */
const OriginGroups = () => {
    const { t } = useTranslation();
    const [data, setData] = useState({ vantages: [], groups: [], bgp: null });
    const [loading, setLoading] = useState(false);
    const [q, setQ] = useState('');

    const load = useCallback(async (query = '') => {
        setLoading(true);
        const r = await cdnProbeService.getOriginGroups(query);
        setLoading(false);
        if (r.success) setData(r.data || { vantages: [], groups: [], bgp: null });
        else message.error(r.error || t('Failed to load'));
    }, [t]);

    useEffect(() => { load(); }, [load]);

    const kindTag = (k) => <Tag color={KIND_COLOR[k]}>{{ bgp: 'BGP', split: t('Split'), nobgp: t('No route') }[k] || k}</Tag>;
    const fpCols = (data.vantages || []).map((v) => ({
        title: <Tooltip title={v.name}><span>{v.name.split('-').slice(0, 2).join('-')}</span></Tooltip>,
        key: `v${v.id}`, width: 88, align: 'right',
        render: (_, r) => { const x = r.fp?.[String(v.id)]; return x == null ? <Text type="secondary">-</Text> : Math.round(x); },
    }));

    const columns = [
        { title: 'ASN', dataIndex: 'asn', width: 100, render: (a) => (a ? `AS${a}` : '-') },
        {
            title: t('BGP prefix'), width: 230,
            render: (_, g) => (
                <Space size={4} wrap>
                    {kindTag(g.kind)}
                    {(g.bgp_prefixes || []).map((p) => <Text key={p} code>{p}</Text>)}
                    {g.merged?.length > 0 && <Tooltip title={g.merged.join('\n')} overlayStyle={{ whiteSpace: 'pre-line' }}><Tag color="green">{t('Merged')} +{g.merged.length}</Tag></Tooltip>}
                </Space>
            ),
        },
        { title: t('Members'), width: 80, align: 'right', render: (_, g) => g.members.length },
        ...fpCols,
    ];

    const memberColumns = [
        {
            title: '/24', dataIndex: 'prefix', width: 230,
            render: (p, m) => <Space size={4}><Text code>{p}</Text>{m.split && <Tag color="orange">{t('Split')}</Tag>}</Space>,
        },
        { title: t('Hosts'), dataIndex: 'hosts', width: 180, ellipsis: true, render: (h) => <Tooltip title={(h || []).join(', ')}>{(h || []).join(', ')}</Tooltip> },
        ...fpCols,
    ];

    const bgp = data.bgp;
    return (
        <Card
            title={<Space>{t('Origin groups')}{bgp && <Text type="secondary" style={{ fontWeight: 'normal', fontSize: 12 }}>BGP: {bgp.file?.split('/').pop()} · {bgp.rows?.toLocaleString()}</Text>}</Space>}
            extra={(
                <Space>
                    <Input.Search size="small" allowClear placeholder={t('Origin IP')} style={{ width: 200 }} value={q}
                        onChange={(e) => setQ(e.target.value)} onSearch={(v) => load(v.trim())} />
                    <Button size="small" icon={<ReloadOutlined />} onClick={() => load(q.trim())} loading={loading} />
                </Space>
            )}
        >
            <Table rowKey="id" size="small" loading={loading} columns={columns} dataSource={data.groups}
                pagination={{ pageSize: 50, hideOnSinglePage: true }} scroll={{ x: 900 }}
                expandable={{
                    expandedRowRender: (g) => (
                        <Table rowKey="prefix" size="small" columns={memberColumns} dataSource={g.members} pagination={false} />
                    ),
                }} />
        </Card>
    );
};

export default OriginGroups;
